import { useMemo } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";

import type { DetailPageTab } from "src/components/layout/detail-page";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { useUser } from "src/contexts/user-context";
import { useHasSupportAccessControl } from "src/hooks/use-has-support-access-control";

/** The workspace settings pages this viewer can open, in display order. */
export const useWorkspaceSettingsTabs = (): DetailPageTab[] => {
  const { user, workspace } = useUser();
  const { isPermitted: hasUpdatePermission } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_update" },
  });
  const { isPermitted: isWorkspaceAdmin } = useResourcePermission({
    v2: { resource: "workspace", grant: "is_workspace_admin" },
  });
  const { permission: userSupportAccessSettingsPermission } =
    useHasSupportAccessControl();
  const { cepLabels } = useFlags();

  const tabs: DetailPageTab[] = useMemo(() => {
    // Staff-only tool: shown to any HT staff (can_impersonate = admin_type IS
    // NOT NULL) and always in local dev, independent of the workspace-admin gate
    // below — an internal user who isn't a workspace admin should still see it.
    const entitlementCenterTabs: DetailPageTab[] =
      user?.can_impersonate || SHOW_ENTITLEMENT_CENTER_LOCALLY
        ? [ENTITLEMENT_CENTER_TAB]
        : [];

    const BASE_TABS: DetailPageTab[] = [
      CLOUD_PROVIDERS_TAB,
      API_KEYS_TAB,
      STORAGE_TAB,
      TUNNELS_TAB,
      // Labels sits between Tunnels and Audit log per the Figma settings design.
      ...(cepLabels ? [LABELS_TAB] : []),
      AUDIT_LOG_TAB,
    ];
    if (hasUpdatePermission || user?.is_impersonating) {
      const canSeeSpaces = workspace?.spaces_enabled && isWorkspaceAdmin;
      return [
        WORKSPACE_TAB,
        MEMBERS_TAB,
        GROUPS_TAB,
        ...(canSeeSpaces ? [SPACES_TAB] : []),
        ...BASE_TABS,
        ...entitlementCenterTabs,
        {
          ...SUPPORT_ACCESS_TAB,
          isHidden: !userSupportAccessSettingsPermission,
        },
      ];
    }
    return [
      MEMBERS_TAB,
      ...entitlementCenterTabs,
      {
        ...SUPPORT_ACCESS_TAB,
        isHidden: !userSupportAccessSettingsPermission,
      },
    ];
  }, [
    userSupportAccessSettingsPermission,
    hasUpdatePermission,
    user?.is_impersonating,
    user?.can_impersonate,
    workspace?.spaces_enabled,
    isWorkspaceAdmin,
    cepLabels,
  ]);

  return tabs;
};

const MEMBERS_TAB: DetailPageTab = {
  title: "Members",
  path: "members",
  variant: "table",
};

const AUDIT_LOG_TAB: DetailPageTab = {
  title: "Audit log",
  path: "audit-log",
  variant: "table",
};

const SUPPORT_ACCESS_TAB: DetailPageTab = {
  title: "Support access",
  path: "support-access",
  variant: "form",
};

const WORKSPACE_TAB: DetailPageTab = {
  title: "Workspace",
  path: "workspace",
  variant: "form",
};

const GROUPS_TAB: DetailPageTab = {
  title: "Groups",
  path: "groups",
  variant: "table",
};

const SPACES_TAB: DetailPageTab = {
  title: "Spaces",
  path: "spaces",
  variant: "table",
};

const LABELS_TAB: DetailPageTab = {
  title: "Labels",
  path: "labels",
  // Two-pane (resource nav + section panel) needs page width, not the narrow
  // "form" width, or the panel shrinks below the Figma's 672px and the nav
  // indents. "card" = grey canvas + constrained page width, for card-heavy pages.
  variant: "card",
};

const CLOUD_PROVIDERS_TAB: DetailPageTab = {
  title: "Cloud providers",
  path: "cloud-providers",
  variant: "table",
};
const API_KEYS_TAB: DetailPageTab = {
  title: "API keys",
  path: "api-keys",
  variant: "table",
};

const STORAGE_TAB: DetailPageTab = {
  title: "Storage",
  path: "storage",
  variant: "form",
};

const TUNNELS_TAB: DetailPageTab = {
  title: "Tunnels",
  path: "tunnels",
  variant: "table",
};

const ENTITLEMENT_CENTER_TAB: DetailPageTab = {
  title: "Entitlement Center",
  path: "entitlement-center",
  variant: "table",
  // Its tables run to the card's bottom, and its save bar sits over them when shown.
  hasBottomPadding: false,
};

// Staff-only tool. In local dev (vite `import.meta.env.DEV`) it's shown to any
// admin so the page can be exercised without seeding an `admin_type`; the
// backend independently enforces staff access in deployed environments.
const SHOW_ENTITLEMENT_CENTER_LOCALLY = import.meta.env.DEV;
